/**
 * AgentPanelPage — the /agent-panel route rendered inside the Collabora
 * editor's #zehn-agent-dock iframe (injected by the fork's branding.js).
 *
 * Reads the file from query params (?file=<uuid>&title=&lang=, posted down by
 * the editor in Zehn_AgentContext as the panelUrl) and hosts DockPanel:
 * zehn-aip's assistant and the template's Placeholders. It reads docs with
 * this origin's session, like any other page of docs — the file too, for the
 * person's edit right on it, decided as the editor decides it (liveState.js:
 * locked, sent for signing or agreement, filed, or below editor is read
 * only) and re-read while the dock is open. The assistant is told that right
 * (`can_edit`); until the first read answers it is no right.
 * Any file opened in an edit session gets the dock — not only some product's
 * documents.
 * Outbound events go up to the collabora frame as Zehn_PanelEvent envelopes;
 * branding.js relays them verbatim to the WOPI host with a locked origin.
 *
 * The request/response protocol with branding.js (the assistant's editor
 * operations, template placeholder bookmarks) lives in hooks/useEditorBridge.js
 * and is handed to DockPanel as `editorBridge`.
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLiveEditState } from '@/modules/collabora-editor/useLiveEditState.js';
import { DockPanel } from '../components/DockPanel.jsx';
import { postPanelEvent, useEditorBridge } from '../hooks/useEditorBridge.js';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function AgentPanelPage() {
  const { t } = useTranslation('assistant');
  const [searchParams] = useSearchParams();
  const fileParam = searchParams.get('file') || '';
  const fileUuid = UUID.test(fileParam) ? fileParam.toLowerCase() : null;
  const title = searchParams.get('title') || '';

  const file = useMemo(
    () => (fileUuid ? { uuid: fileUuid, title } : null),
    [fileUuid, title],
  );

  const editorBridge = useEditorBridge();
  const live = useLiveEditState(fileUuid, { requested: 'edit', enabled: Boolean(fileUuid) });
  const canEdit = Boolean(live.node) && live.mode === 'edit';

  const handleDocumentChanged = useCallback(() => {
    postPanelEvent({ type: 'document_changed', fileUuid: file?.uuid ?? null });
  }, [file]);

  const handleCollapse = useCallback(() => {
    postPanelEvent({ type: 'close_panel' });
  }, []);

  if (!file) {
    return (
      <div className="flex h-screen items-center justify-center bg-white text-xs text-muted-foreground">
        {t('agent.missingContext')}
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col">
      <DockPanel
        file={file}
        canEdit={canEdit}
        editorBridge={editorBridge}
        onDocumentChanged={handleDocumentChanged}
        onCollapse={handleCollapse}
      />
    </div>
  );
}
